import React, { useMemo } from "react";
import { Box } from "@mui/material";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
} from "recharts";

const COLOR_MAP: Record<string, string> = {
    "50% - 74%": "#dad716",
    "90% and above": "var(--primary-color)",
    "75% - 89%": "var(--secondary-color)",
    "Below 50%": "var(--third-color)",
};

interface DistributionItem {
    label: string;
    count: number;
    percentage?: number;
}

interface DonutChartProps {
    data2?: {
        distribution?: DistributionItem[];
        totalReports?: number;
    };
}

const DonutChart = ({ data2 }: DonutChartProps) => {
    const distribution = data2?.distribution ?? [];

    const total =
        data2?.totalReports ??
        distribution.reduce(
            (sum, item) => sum + (item?.count ?? 0),
            0
        );

    const data = useMemo(
        () =>
            distribution.map((item) => ({
                label: item.label,
                value: item.count,
                percentage: item.percentage,
                color: COLOR_MAP[item.label] ?? "#999999",
            })),
        [distribution]
    );

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
            }}
        >
            {/* Donut */}
            <Box
                className="w-50 m-0 p-0"
                sx={{
                    maxWidth: "400px",
                    height: 300,
                }}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            innerRadius="58%"
                            outerRadius="82%"
                            paddingAngle={0}
                            stroke="#F7F3EB"
                            strokeWidth={2}
                            isAnimationActive={false}
                        >
                            {data.map((item) => (
                                <Cell
                                    key={item.label}
                                    fill={item.color}
                                />
                            ))}
                        </Pie>

                        <Tooltip
                            content={({ active, payload }) => {
                                if (
                                    !active ||
                                    !payload ||
                                    !payload.length
                                ) {
                                    return null;
                                }

                                const item = payload[0]?.payload;

                                return (
                                    <div
                                        style={{
                                            background: "#fff",
                                            padding: "10px 12px",
                                            borderRadius: "8px",
                                            boxShadow:
                                                "0 2px 10px rgba(0,0,0,.15)",
                                            fontSize: "13px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontWeight: 600,
                                                marginBottom: 4,
                                            }}
                                        >
                                            {item.label}
                                        </div>

                                        <div>
                                            Reports:{" "}
                                            <b>{item.value}</b>
                                        </div>

                                        <div>
                                            Percentage:{" "}
                                            <b>
                                                {Math.round(
                                                    (item.value /
                                                        total) *
                                                    100
                                                )}
                                                %
                                            </b>
                                        </div>
                                    </div>
                                );
                            }}
                        />

                        {/* Center Total */}
                        <text

                            x="50%"
                            y="55%"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="#222"
                            fontSize="28px"
                            fontWeight={600}
                        >
                            {total}
                        </text>
                        <text
                            x="50%"
                            y="45%"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="#666"
                            fontSize="12px"
                            fontWeight={500}
                        >
                            Total Reports
                        </text>

                    </PieChart>
                </ResponsiveContainer>
            </Box>

            {/* Custom Legend */}
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {data.map((item) => (
                    <div
                        key={item.label}
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "18px 1fr 40px 55px",
                            alignItems: "center",
                            columnGap: 0,
                            marginBottom: 5,
                        }}
                    >
                        {/* Color indicator */}
                        <span
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                background: item.color,
                            }}
                        />

                        {/* Label */}
                        <span
                            style={{
                                fontSize: "14px",
                                color: "#222",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {item.label}
                        </span>

                        {/* Count */}
                        <span
                            style={{
                                textAlign: "right",
                                fontWeight: 600,
                                fontSize: "16px",
                                color: "var(--primary-color)",
                            }}
                        >
                            {item.value}
                        </span>

                        {/* Percentage */}
                        <span
                            style={{
                                textAlign: "right",
                                color: "#666",
                                fontSize: "14px",
                            }}
                        >
                            (
                            {total
                                ? Math.round(
                                    (item.value / total) * 100
                                )
                                : 0}
                            %)
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DonutChart;
